import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Input, TextArea } from '@trackman/web-shared-components';
import ImageUploader from 'components/Admin/ImageUploader/ImageUploader';
import { useServices, useStores } from 'index';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getErrorMessage } from 'utilities/ErrorHelper';
import useSetPageTitle from 'utilities/useSetPageTitle';

export const EditPartner = () => {
  useSetPageTitle({ title: 'Edit Partner' });

  const queryClient = useQueryClient();
  const partnerId = useParams().partnerId;

  const notificationsStore = useStores().notificationStore;
  const partnersEditorService = useServices().partnersEditorService;
  const { data: adminPartner } = useQuery({
    queryKey: ['adminPartner', partnerId],
    queryFn: async () => {
      const response = await partnersEditorService.getPartner(partnerId ?? '');
      setFullName(response.fullName);
      setDescription(response.description);
      setImageUrl(response.imageUrl ?? '');
      return response;
    },
    enabled: !!partnerId,
    refetchOnWindowFocus: false,
  });

  const [fullName, setFullName] = useState(adminPartner?.fullName ?? '');
  const [description, setDescription] = useState(adminPartner?.description ?? '');
  const [imageUrl, setImageUrl] = useState<string>(adminPartner?.imageUrl ?? '');
  const [imgFile, setImgFile] = useState<File | undefined>(undefined);

  const handleUploadImage = (data: File) => {
    setImgFile(data);
  };

  const uploadedImage = imgFile ? { id: '', data: imgFile } : undefined;

  const savePartner = useMutation({
    mutationFn: () =>
      !!partnerId
        ? partnersEditorService.saveExistingPartner(existingPartnerPayload)
        : partnersEditorService.saveNewPartner(newPartnerPayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.refetchQueries({ queryKey: ['partners'] });
      notificationsStore.addToast(
        'success',
        !!partnerId ? 'Existing partner has been saved successfully' : 'New partner has been saved successfully',
        'success'
      );
    },
    onError: (ex) => notificationsStore.addToast('error', getErrorMessage(ex), 'error'),
  });

  const newPartnerPayload = {
    file: uploadedImage,
    fullName,
    description,
  };

  const existingPartnerPayload = {
    id: partnerId!,
    file: uploadedImage,
    fullName,
    description,
    imageUrl,
  };

  return (
    <>
      <div className='default-page-header'>
        <h1 className='page-title'>Edit Partner</h1>
      </div>
      <div className='admin-content-container'>
        <div className='admin-edit-section'>
          <h2>Partner's name</h2>
          <div className='inputs-container'>
            <div className='input-wrapper'>
              <Input label={`Full name`} value={fullName} onChange={(e) => setFullName(e.currentTarget.value)} />
            </div>
          </div>
        </div>

        <div className='admin-edit-section'>
          <h2>Description</h2>
          <TextArea value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
        </div>

        <div className='admin-edit-section'>
          <h2>Partner's image</h2>
          <ImageUploader imageData={uploadedImage} imageUrl={imageUrl} handleUploadImage={handleUploadImage} />
        </div>
        <div className='admin-footer'>
          <button
            className='styled-button primary'
            onClick={() => savePartner.mutate()}
            disabled={fullName === '' || (!imageUrl && imgFile === undefined)}
          >
            Save changes
          </button>
        </div>
      </div>
    </>
  );
};
